import type { AgentQuota, AgentStatus, AgentTurnStatus } from '@/lib/api/types';

/** Show the meter once this much of the month's allowance is gone. */
const METER_THRESHOLD = 0.8;

/**
 * Where the active conversation id is remembered, per workspace.
 *
 * sessionStorage, not localStorage: a conversation belongs to this tab's visit,
 * and the id is only valid for the workspace in the current JWT — the backend
 * 404s a conversation from another tenant.
 */
export function conversationStorageKey(tenantId: string): string {
  return `assistant:${tenantId}`;
}

/** What to tell the user about how a turn ended, or nothing. */
export function turnNotice(status: AgentTurnStatus): string | null {
  switch (status) {
    case 'stopped':
      // Two-valued on the wire: a user Stop and a resolution with nothing left
      // to deliver both arrive as `stopped`. Describe the stream, not a motive.
      return 'No reply was produced.';
    case 'step_limit':
      return 'That took more steps than one turn allows — ask a narrower question, or say “continue”.';
    case 'completed':
    case 'awaiting_confirmation':
    // `failed` is always preceded by an `error` event carrying the real message.
    case 'failed':
      return null;
  }
}

/**
 * Whether the launcher is shown at all.
 *
 * `enabled: false` is a missing API key or a paused budget. A `limit` of 0 is a
 * workspace with no allowance at all — an enterprise contract that never
 * negotiated one (D2a), or an admin's Off switch. It is rare (free 50, premium
 * 1000, enterprise 0), but it arrives as `enabled: true` with
 * `reason: "quota_exhausted"` exactly like a spent allowance, so it needs its
 * own carve-out. A spent positive allowance keeps its bubble: that person can
 * open it and read why.
 */
export function assistantIsVisible(status: AgentStatus | undefined): boolean {
  if (status?.enabled !== true) return false;
  return status.quota.limit !== 0;
}

export interface ComposerBlock {
  message: string;
  upgradeUrl: string | null;
}

/**
 * Why the composer cannot send, or `null`.
 *
 * `limit === 0` is a workspace with no allowance at all — an unnegotiated
 * enterprise contract (D2a) or an admin's Off switch; a positive limit that has
 * been reached is a spent allowance that comes back next month. They read as the same `quota_exhausted` reason, so the
 * limit itself is what tells them apart.
 */
export function composerBlock(status: AgentStatus | undefined): ComposerBlock | null {
  if (!status || status.reason !== 'quota_exhausted') return null;

  if (status.quota.limit === 0) {
    return {
      message: 'The assistant is not switched on for this workspace.',
      upgradeUrl: status.upgrade_url || null,
    };
  }
  return {
    message: 'You have used this month’s assistant messages. The allowance resets next month.',
    upgradeUrl: status.upgrade_url || null,
  };
}

export interface QuotaMeter {
  used: number;
  limit: number;
  percent: number;
}

/** The meter, once the allowance is nearly gone. Unlimited and off never show one. */
export function quotaMeter(quota: AgentQuota): QuotaMeter | null {
  const { used, limit } = quota;
  if (limit === null || limit <= 0) return null;
  const percent = Math.min(100, Math.round((used / limit) * 100));
  return used / limit >= METER_THRESHOLD ? { used, limit, percent } : null;
}
